import {TaxSummary} from "../WealthTransfer/models/api";

export const enum FailedAPICall {
    ESTIMATED_IMPACT = "Estimated Impact",
    REFERENCE_DATA = "Reference Data",
    PAYOUT_PERCENTAGES = "Payout Percentages",
}

export const enum StrategyType {
    STATE_OF_RESIDENCY = "STATE_OF_RESIDENCY",
    ANNUAL_GIFTING = "ANNUAL_GIFTING",
    GIFT_TO_GRANTOR_TRUST = "GTGT",
    TWO_YEAR_GRAT = "TWO_YEAR_GRAT",
    FIVE_YEAR_GRAT = "FIVE_YEAR_GRAT",
    ROLLING_GRAT = "ROLLING_GRAT"
}

export type RateDto = {
    id: string,
    description: string,
    value: number,
}

export type GrowthRates = {
    rateDtos: RateDto[],
};

export type HurdleRateDTO = {
    hurdleRate: number,
    hurdleRateMonth: string,
    hurdleRateYear: string
};

export type GRATRates = {
    growthRates: GrowthRates,
    hurdleRateDTO: HurdleRateDTO
}

export type StrategyTimeFrame = {
    strategyId: string | undefined,
    strategyType: StrategyType,
    enabled: boolean;
    contributionAmount: number,
};

export type EstimatedImpact = {
    amountToBeneficiaries: number | undefined,
    impactToEstEstateTax: number | undefined,
    strategyPayoutSchedule: StrategyPayoutScheduleItem[],
};

export const initialEstimatedImpact: EstimatedImpact = {
    amountToBeneficiaries: undefined,
    impactToEstEstateTax: undefined,
    strategyPayoutSchedule: [],
};

export type GiftAndExemptionSummary = {
    availableExemption: number | undefined,
    federalExemptionUsed: number | undefined,
    federalExemptionLeft: number | undefined,
    estFederalGiftTax: number | undefined,
}

export type GiftToGrantorTrustEstimatedImpact = EstimatedImpact & {
    giftAndExemptionSummary: GiftAndExemptionSummary
}

export const initialGTGTEstimatedImpact = {
    ...initialEstimatedImpact,
    giftAndExemptionSummary: {
        availableExemption: undefined,
        federalExemptionUsed: undefined,
        federalExemptionLeft: undefined,
        estFederalGiftTax: undefined
    }
}

export type FutureEstateValue = {
    totalAmountToBeneficiaries: number,
    totalEstEstateTax: number
}

export const initialFutureEstateValue: FutureEstateValue = {
    totalAmountToBeneficiaries: 0,
    totalEstEstateTax: 0
}

// State of Residency Strategy Types
export type StateInputDto = {
    id: string,
    name: string,
    forecastedEstateTaxRate: number
};

export type StateEstateTransferRates = {
    stateInputsDTOs: StateInputDto[],
};

export type StateOfResidencyStrategy = {
    id: string | undefined,
    description: string,
    selectedStateOfResidency: string,
    estimatedImpact: SOREstimatedImpact;
    enabled: boolean;
}

export type SOREstimatedImpact = EstimatedImpact & {
    graduatedEffectiveStateTaxRate: number | undefined,
    effectiveRate: number | undefined
}

export const initialSOREstimatedImpact: SOREstimatedImpact = {
    ...initialEstimatedImpact,
    graduatedEffectiveStateTaxRate: undefined,
    effectiveRate: undefined
}

export const initialStateOfResidencyStrategy: StateOfResidencyStrategy = {
    id: undefined,
    description: "Change of Residency",
    selectedStateOfResidency: "",
    estimatedImpact: initialSOREstimatedImpact,
    enabled: false,
}

// Profile State Effective Rate Type
export type ProfileStateEffectiveRate = {
    stateOfResidency: string | undefined,
    effectiveRate: number | undefined
}

export const initialProfileStateEffectiveRate: ProfileStateEffectiveRate = {
    stateOfResidency: undefined,
    effectiveRate: undefined
}

// Annual Gifting Strategy Types
export type AnnualGiftingStrategy = StrategyTimeFrame & {
    sequenceNumber?: number,
    description: string,
    yearsOfFlow: number,
    yearsUntilFlow: number,
    estimatedImpact: EstimatedImpact
}

export const initialAnnualGiftingStrategy: AnnualGiftingStrategy = {
    strategyId: undefined,
    strategyType: StrategyType.ANNUAL_GIFTING,
    description: "Annual Gifting",
    contributionAmount: 0,
    yearsUntilFlow: 0,
    yearsOfFlow: 1,
    estimatedImpact: initialEstimatedImpact,
    enabled: true
}

// Gift to Grantor Trust Strategy Types
export type GiftToGrantorTrustStrategy = StrategyTimeFrame & {
    sequenceNumber?: number,
    description: string,
    yearsOfGrantorStatus: number | undefined
    estimatedImpact: GiftToGrantorTrustEstimatedImpact
}

export const initialGiftToGrantorTrustStrategy: GiftToGrantorTrustStrategy = {
    strategyId: undefined,
    strategyType: StrategyType.GIFT_TO_GRANTOR_TRUST,
    description: "Gift to Grantor Trust",
    contributionAmount: 0,
    yearsOfGrantorStatus: undefined,
    estimatedImpact: initialGTGTEstimatedImpact,
    enabled: true
}

// GRAT Strategy Types
export type GRATStrategy = StrategyTimeFrame & {
    sequenceNumber?: number,
    description: string,
    increaseInPayment: number,
    annuityRate: number | undefined,
    yearsOfFlow: number,
    estimatedImpact: EstimatedImpact
}

export const initialTwoYearGRATStrategy: GRATStrategy = {
    strategyId: undefined,
    strategyType: StrategyType.TWO_YEAR_GRAT,
    description: "2-Year GRAT",
    contributionAmount: 0,
    increaseInPayment: 20,
    annuityRate: undefined,
    yearsOfFlow: 2,
    estimatedImpact: initialEstimatedImpact,
    enabled: true
}

export const initialFiveYearGRATStrategy: GRATStrategy = {
    strategyId: undefined,
    strategyType: StrategyType.FIVE_YEAR_GRAT,
    description: "5-Year GRAT",
    contributionAmount: 0,
    increaseInPayment: 20,
    annuityRate: undefined,
    yearsOfFlow: 5,
    estimatedImpact: initialEstimatedImpact,
    enabled: true
}

export const initialRollingGRATStrategy: GRATStrategy = {
    strategyId: undefined,
    strategyType: StrategyType.ROLLING_GRAT,
    description: "Rolling GRAT",
    contributionAmount: 0,
    increaseInPayment: 20,
    annuityRate: undefined,
    yearsOfFlow: 3,
    estimatedImpact: initialEstimatedImpact,
    enabled: true
}

export type PayoutPercentagesRequestDTO = {
    contributionAmount: number,
    increaseInPayment: number,
    annuityRate: number,
    strategyType: StrategyType
}

export type PayoutPercentagesResponseDTO = {
    payoutPercentages: number[]
}

export const initialPayoutPercentagesTwo: PayoutPercentagesResponseDTO = {
    payoutPercentages: [0.0, 0.0]
}
export const initialPayoutPercentagesFive: PayoutPercentagesResponseDTO = {
    payoutPercentages: [0.0, 0.0, 0.0, 0.0, 0.0]
}

export type GRATEstimatedImpactRequestDTO = StrategyTimeFrame & PayoutPercentagesResponseDTO & {
    yearsOfFlow: number
}

export type StrategyPayoutScheduleItem = {
    index: number,
    contributionAmount: number,
    year1Annuity: number,
    year2Annuity: number,
    remainder: number
}

// WealthPO Summary Types
export type StrategyDetailsSectionInfo = {
    id: string | undefined,
    description: string,
    type: StrategyType,
    amountToBeneficiaries: number,
    impactToEstEstateTax: number,
    enabled: boolean;
}

export type EstimatedImpactSummary = {
    onlyStateOfResidencyStrategySelected: boolean;
    totalImpactToAmountToBeneficiaries: number | undefined,
    totalImpactToEstEstateTax: number | undefined,
}
export const initialEstimatedImpactSummary: EstimatedImpactSummary = {
    onlyStateOfResidencyStrategySelected: false,
    totalImpactToAmountToBeneficiaries: undefined,
    totalImpactToEstEstateTax: undefined,
}

export type EstateTaxBreakdown = {
    estateValue: number;
    estimatedEstateTax: number;
    charitableDeduction: number;
    beneficiaryTotalValue: number;
    federalTaxSummary: TaxSummary;
    stateTaxSummary: TaxSummary;
}

export const initialEstateTaxBreakdown: EstateTaxBreakdown = {
    estateValue: 0,
    estimatedEstateTax: 0,
    charitableDeduction: 0,
    beneficiaryTotalValue: 0,
    federalTaxSummary: {
        effectiveTaxRate: 0,
        estimatedEstateTax: 0,
        taxableEstateValue: 0,
        totalAvailableExemption: 0
    },
    stateTaxSummary: {
        effectiveTaxRate: 0,
        estimatedEstateTax: 0,
        taxableEstateValue: 0,
        totalAvailableExemption: 0
    }
}

export type WealthPOSummaryResponse = {
    stateOfResidencyStrategy: StateOfResidencyStrategy;
    annualGiftingStrategies: AnnualGiftingStrategy[];
    giftToGrantorTrustStrategies: GiftToGrantorTrustStrategy[];
    gratStrategies: GRATStrategy[]
    estimatedImpactSummary: EstimatedImpactSummary;
    estateTaxBreakdownWithoutStrategies: EstateTaxBreakdown;
    estateTaxBreakdownWithStrategies: EstateTaxBreakdown;
    profileStateEffectiveRate: ProfileStateEffectiveRate;
}
export const initialWealthPOSummaryResponse: WealthPOSummaryResponse = {
    stateOfResidencyStrategy: initialStateOfResidencyStrategy,
    annualGiftingStrategies: [],
    giftToGrantorTrustStrategies: [],
    gratStrategies: [],
    estimatedImpactSummary: initialEstimatedImpactSummary,
    estateTaxBreakdownWithoutStrategies: initialEstateTaxBreakdown,
    estateTaxBreakdownWithStrategies: initialEstateTaxBreakdown,
    profileStateEffectiveRate: initialProfileStateEffectiveRate
};